import { PermissionRole, BlockType, BlockStatus } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { FilePreview } from 'src/components/file-preview';
import { useGetPageId } from 'src/utils/getPageId';
import { useUploadDriveFiles } from 'src/hooks/space/use-upload';
import { transaction } from 'src/hooks/use-transaction';
import { useCurrentSpace, useUpdateSpace } from 'src/hooks/space';
import { useCreateDocPage, useFetchPage, useFetchRootPages } from 'src/hooks/page';
import { $currentUserCache } from 'src/services/user/current-user';
import { useHistory } from 'react-router-dom';
import { setAppUiState, usePDFHistorySideBar } from 'src/services/app';
import { Header } from '../main/header';
import { ToSharedPage } from 'src/components/empty/to-shared-page';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { usePageMeta } from 'src/hooks/page/use-page-meta';
import { v4 } from 'uuid';
import { PDFUploadArea } from './upload-area';
import { PDFSidebar } from './sidebar';
import { useEnableAI } from 'src/hooks/block/use-enable-AI';
import { useFetchAiConfig } from 'src/hooks/page/use-fetch-ai-config';
import { set_TocComponent } from 'src/components/move-to/toc-list';
import { Toc } from '../main/aside/toc';
import { usePickBlock } from 'src/utils/pick-block';
import { request } from 'src/common/request';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { setLocalStorage } from '@flowus/common/utils/local-storage';
import { PDF_HISTORY_SIDEBAR } from 'src/common/const';
import { sleep } from '@flowus/common/async';

set_TocComponent(Toc);

export const FilePreviewPage: FC = () => {
  const fileId = useGetPageId();
  const history = useHistory();
  const block = usePickBlock(fileId, ['status']);
  const { roleWithoutPublic, shared } = usePermissions(fileId);
  const pageMeta = usePageMeta(fileId);
  const uploadDrive = useUploadDriveFiles();
  const space = useCurrentSpace();
  const createPage = useCreateDocPage();
  const updateSpace = useUpdateSpace();
  const pdfHistorySideBar = usePDFHistorySideBar();
  const [blockId, setBlockId] = useState<string>('');
  const [file, setFile] = useState<File>();
  const { enableAI } = useEnableAI();
  const fetchPage = useFetchPage();
  const fetchSpaceRootPages = useFetchRootPages();

  useFetchAiConfig();

  useEffect(() => {
    if (block?.status === BlockStatus.DELETED_COMPLETE) {
      history.push('/preview');
    }
  }, [block?.status, history]);

  useEffect(() => {
    if (!fileId) {
      setAppUiState({ $pdfHistorySidebar: true });
      setLocalStorage(PDF_HISTORY_SIDEBAR, 'true');
    }
  }, [fileId]);

  useEffect(() => {
    if (!space.uuid) return;
    void fetchSpaceRootPages(space.uuid).then(() => {
      setAppUiState({ $pageRootLoaded: true });
    });
  }, [fetchSpaceRootPages, space.uuid]);

  useEffect(() => {
    // 创建默认文件夹
    let parentId: string | undefined = space.setting ? space.setting.pdfFolderId : '';
    if (!parentId) {
      parentId = createPage(BlockType.FOLDER, { parentId: space.uuid, last: true });
      transaction(() => {
        void updateSpace(space.uuid, {
          setting: {
            ...space.setting,
            pdfFolderId: parentId,
          },
        });
      });
    } else {
      void fetchPage(parentId);
    }
  }, [createPage, fetchPage, space.setting, space.uuid, updateSpace]);

  const handleUpload = (files: FileList | File[]) => {
    if (fileId) {
      history.push('/preview');
    }

    const parentId = space.setting ? space.setting.pdfFolderId : '';
    if (!parentId) return parentId;

    const file = files[0];
    if (!file) return;

    setFile(file);

    const blockId = v4();
    setBlockId(blockId);

    void uploadDrive({
      ownerPageId: parentId,
      files: [file],
      type: 'file',
      spaceId: space.uuid,
      blockId,
      userId: $currentUserCache.uuid,
      onComplete: async (result) => {
        if (result.success) {
          setTimeout(() => {
            void request.ai.getSpacePdfHistory(getCurrentSpaceId()).then((res) => {
              setAppUiState({ $pdfAIHistory: res.results });
            });
          }, 1000);

          setFile(undefined);
          setBlockId('');
          await sleep(1000);
          history.push(`/preview/${blockId}`);
        }
      },
      inTransferList: true,
    });
  };

  const retry = () => {
    if (file) {
      handleUpload([file]);
    }
  };

  // 如果当前没有浏览权限并且此页面开启了分享，寻味是否要去share
  if ((pageMeta?.tag === 'NO_PERMISSION' || roleWithoutPublic === PermissionRole.NONE) && shared) {
    return (
      <>
        <Header />
        <ToSharedPage />
      </>
    );
  }

  return (
    <div className="flex h-full">
      {enableAI && pdfHistorySideBar && <PDFSidebar handleUpload={handleUpload} />}
      {fileId ? (
        <FilePreview isPreviewPage uuid={fileId} />
      ) : (
        <PDFUploadArea
          retry={retry}
          blockId={blockId}
          setBlockId={setBlockId}
          handleUpload={handleUpload}
        />
      )}
    </div>
  );
};
